import { useEffect, useRef, useState } from 'react';
import { Globe, Check } from 'lucide-react';
import { useLanguage } from '@/lib/useLanguage';

/**
 * Globe icon that opens a tiny language menu. Writes both i18n state and
 * (if signed in) the persistent profile.preferred_language column.
 */
export function LanguageSwitcher() {
  const { language, setLanguage, languages } = useLanguage();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = languages.find((l) => l.code === language) ?? languages[0]!;

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Language: ${current.label}`}
        className="inline-flex size-9 items-center justify-center rounded-[10px] border border-[color:var(--color-border)] bg-surface-2 text-on-surface transition hover:text-primary"
      >
        <Globe className="size-[18px]" aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+8px)] z-30 flex min-w-[180px] flex-col overflow-hidden rounded-2xl border border-[color:var(--color-border)] bg-surface-2 py-1 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.6)]"
        >
          {languages.map((l) => (
            <button
              key={l.code}
              type="button"
              role="menuitemradio"
              aria-checked={l.code === language}
              onClick={() => {
                void setLanguage(l.code);
                setOpen(false);
              }}
              className={[
                'flex items-center justify-between gap-3 px-4 py-2 text-left text-sm transition',
                l.code === language
                  ? 'text-on-surface'
                  : 'text-on-surface-dim hover:bg-surface-3 hover:text-on-surface',
              ].join(' ')}
            >
              <span className="flex items-center gap-2">
                <span aria-hidden>{l.flag}</span>
                {l.label}
              </span>
              {l.code === language && (
                <Check className="size-4 text-primary" aria-hidden />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default LanguageSwitcher;
